/**
 * Keeps the installed PWA on the latest deploy without a manual reload.
 *
 * An installed PWA that stays open (or resumes from the background) rarely
 * re-checks its service worker — the browser only does that on a navigation —
 * so a deploy could sit unseen until the user happened to reload. Two pieces:
 *
 *  - wireUpdateChecks: ask the registration for an update whenever the app comes
 *    back to the foreground, and on an interval while it stays open.
 *  - wireAutoApply: once a new version is waiting, apply it at a safe moment —
 *    right away if the app is in the background, otherwise the next time it is
 *    hidden — so nothing reloads under the user mid-task. (The in-app "Reload"
 *    button still applies it immediately.)
 */

const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000

type Registration = { update: () => Promise<unknown> }
type Doc = Pick<Document, "visibilityState" | "addEventListener" | "removeEventListener">

export function wireUpdateChecks(
  registration: Registration,
  doc: Doc = document,
  intervalMs: number = UPDATE_CHECK_INTERVAL_MS,
): () => void {
  const check = () => {
    // A failed check (offline, flaky network) is harmless; the next one retries.
    registration.update().catch(() => {})
  }
  const onVisibility = () => {
    if (doc.visibilityState === "visible") check()
  }
  doc.addEventListener("visibilitychange", onVisibility)
  const timer = setInterval(check, intervalMs)
  return () => {
    doc.removeEventListener("visibilitychange", onVisibility)
    clearInterval(timer)
  }
}

export function wireAutoApply(
  isPending: () => boolean,
  apply: () => void,
  doc: Doc = document,
): () => void {
  let applied = false
  const maybeApply = () => {
    if (!applied && isPending() && doc.visibilityState === "hidden") {
      applied = true
      apply()
    }
  }
  maybeApply()
  doc.addEventListener("visibilitychange", maybeApply)
  return () => doc.removeEventListener("visibilitychange", maybeApply)
}
